import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { FC } from "react";
import { useMemo } from "react";
import type { GridViewRegistration } from "../../../api/registrations.types";
import type { Answer, Form, QuestionCard } from "../../../dynamicForm";
import { DynamicGrid } from "../../../dynamicGrid";
import type { SidePanelGroup } from "../../../dynamicGrid/SidePanels";
import { logError } from "../../../api/logError";
import { getFormatedPercentage, StatsList, StatsListItem } from "./StatsList";
import { SidePanelGrid } from "./SidePanelGrid";

export const RiskFactorsSection: FC<{ filteredRowsData: GridViewRegistration[]; form: Form }> = ({
  filteredRowsData,
  form,
}) => {
  const { t } = useTranslation();
  const goToSidePanel = DynamicGrid.useGoToSidePanel();

  const { registrationsWithRiskAnswersByTag, questionCardByTag } = useMemo(
    () => getRiskFactorsInfo(filteredRowsData, form),
    [filteredRowsData, form]
  );

  const riskFactorAnswerCountsAndTag = RISK_TAGS.map(tag => {
    const questionCard = questionCardByTag[tag];
    if (!questionCard) return null;

    const count = registrationsWithRiskAnswersByTag[tag].length;
    return { count, tag };
  }).filter(elem => elem !== null);

  if (riskFactorAnswerCountsAndTag.length === 0) return null;

  return (
    <StatsList label={t("RegistrationGrid.SidePanels.RisksFactorsHeader")}>
      {riskFactorAnswerCountsAndTag.map(({ tag, count }) => (
        <StatsListItem
          key={tag}
          onClick={() => goToSidePanel(tag)}
          label={t(riskTagLabelKey(tag))}
          formattedPercentage={getFormatedPercentage(count, filteredRowsData.length)}
          count={count}
        />
      ))}
    </StatsList>
  );
};

type RiskFactorsSubSidePanelProps = {
  riskTag: RiskTag;
  filteredRowsData: GridViewRegistration[];
  sidePanelGroup: SidePanelGroup;
  form: Form;
};
export const RiskFactorsSubSidePanel = ({
  riskTag,
  filteredRowsData,
  sidePanelGroup,
  form,
}: RiskFactorsSubSidePanelProps) => {
  const { t } = useTranslation();

  const gridApiRef = DynamicGrid.useInitialisedGridApiRef();

  const { registrationsWithRiskAnswersByTag, questionCardByTag, riskFactorAnswerByTag } = useMemo(
    () => getRiskFactorsInfo(filteredRowsData, form),
    [filteredRowsData, form]
  );

  const questionCard = questionCardByTag[riskTag];
  const registrations = registrationsWithRiskAnswersByTag[riskTag];
  const riskFactorAnswer = riskFactorAnswerByTag[riskTag];

  if (!questionCard) return null;
  return (
    <SidePanelGrid
      gridHeader={t(riskTagLabelKey(riskTag))}
      sidePanelGroup={sidePanelGroup}
      registrations={registrations}
      onShowInTableClick={
        riskFactorAnswer
          ? () => {
              if (riskFactorAnswer.type == "yesOrNoInput") {
                gridApiRef.current.upsertFilterItem({
                  field: questionCard.id,
                  operator: "is",
                  value:
                    riskFactorAnswer.value === "yes"
                      ? t("DynamicForm.YesOrNoInput.YesLabel")
                      : t("DynamicForm.YesOrNoInput.NoLabel"),
                });
              }
              if (riskFactorAnswer.type == "dynamicMultipleChoiceInput") {
                const childrenAnswer = riskFactorAnswer.value?.find(({ value }) => value === CHILDREN_VALUE);
                if (!childrenAnswer) return;
                gridApiRef.current.upsertFilterItem({
                  field: questionCard.id,
                  operator: "contains",
                  value: childrenAnswer.label,
                });
              }
            }
          : undefined
      }
    />
  );
};

/** The controller registration is using a dynamic multiple choice input with a option for "Children",
 *  This solution is not ideal but for now there is no other way to solve it.
 *  And, BE(as of this writing) claims that the value is not going to change. */
const CHILDREN_VALUE = "e698e97f-7e62-4633-a647-ae9deea68c89";

export const RISK_TAGS = [
  "CHILDRENS_DATA",
  "THIRD_COUNTRY_TRANSFER",
  "DATA_ON_CRIME",
  "SPECIAL_CATEGORIES_OF_PERSONAL_DATA",
  "AUTOMATED_DECISION_MAKING_YES_OR_NO",
  "INFORMATION_TO_DATA_SUBJECTS_YES_OR_NO",
  "CATEGORIES_OF_DATA_SUBJECTS",
] as const;

export type RiskTag = (typeof RISK_TAGS)[number];

const riskTagLabelKey = (tag: RiskTag): TranslationKey =>
  `RegistrationGrid.SidePanels.RisksFactors.${tag === "CATEGORIES_OF_DATA_SUBJECTS" ? "CHILDRENS_DATA" : tag}`;

const getQuestionCardsByRiskTags = (form: Form) => {
  const questionCardsByTags: Partial<Record<RiskTag, QuestionCard>> = {};

  form.sections.forEach(section => {
    section.questionCards.forEach(questionCard => {
      if (questionCard.tag && RISK_TAGS.some(tag => tag === questionCard.tag))
        questionCardsByTags[questionCard.tag as RiskTag] = questionCard;
    });
  });

  return questionCardsByTags;
};

const getRiskFactorsInfo = (filteredRowsData: GridViewRegistration[], form: Form) => {
  const registrationsWithRiskAnswersByTag = Object.fromEntries<GridViewRegistration[]>(
    RISK_TAGS.map(tag => [tag, []])
  ) as Record<RiskTag, GridViewRegistration[]>;
  const riskFactorAnswerByTag: Partial<Record<RiskTag, Answer>> = {};

  const questionCardByTag = getQuestionCardsByRiskTags(form);

  filteredRowsData.forEach(registration => {
    const registrationAnswerMap = new Map<string, Answer>();
    registration.answers.forEach(answer => registrationAnswerMap.set(answer.questionId, answer));

    RISK_TAGS.forEach(tag => {
      const questionCard = questionCardByTag[tag];
      if (!questionCard) return;

      const answer = registrationAnswerMap.get(questionCard.id);
      if (!answer || answer.value === null) return;

      switch (answer.type) {
        case "yesOrNoInput":
          if (answer.value === "yes") {
            registrationsWithRiskAnswersByTag[tag].push(registration);
            riskFactorAnswerByTag[tag] = answer;
          }
          break;
        case "dynamicMultipleChoiceInput":
          if (answer.value.some(({ value }) => value === CHILDREN_VALUE)) {
            riskFactorAnswerByTag[tag] = answer;
            registrationsWithRiskAnswersByTag[tag].push(registration);
          }
          break;
        default:
          logError(`Unhandled risk answer: ${answer}`);
      }
    });
  });
  return { registrationsWithRiskAnswersByTag, questionCardByTag, riskFactorAnswerByTag };
};
